import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Fragment, Slice } from 'prosemirror-model';
import sanitizeHtml from 'sanitize-html';

export const ConvertNewLineOnPaste = Extension.create({
  name: 'ConvertNewLineOnPaste',

  addOptions() {
    return {
      treatDoubleNewlineAsParagraph: true,
      sanitizeOptions: {
        allowedTags: [
          'b',
          'i',
          'em',
          'strong',
          'u',
          'a',
          'br',
          'p',
          'div',
          'span',
          'ul',
          'ol',
          'li',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
        ],
        allowedAttributes: {
          a: ['href'],
          span: ['style'],
          div: ['style'],
          p: ['style'],
        },
        allowedStyles: {
          '*': {
            'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
          },
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('ConvertNewLineOnPaste'),
        props: {
          handlePaste(view, event) {
            const plainText = event.clipboardData?.getData('text/plain');
            const html = event.clipboardData?.getData('text/html');
            if (html || !plainText) return false;

            const { schema } = view.state;
            if (!schema.nodes.hardBreak || !schema.nodes.paragraph)
              return false;

            const lines = plainText.split(/\r?\n/);
            const nodes = [];

            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              if (line.trim() !== '') {
                nodes.push(schema.text(line));
              }
              nodes.push(schema.nodes.hardBreak.create());
            }

            if (nodes.length > 0) nodes.pop(); // optional trailing break

            const fragment = Fragment.fromArray(nodes);
            const sliceWithBreaks = new Slice(fragment, 0, 0);
            const tr = view.state.tr.replaceSelection(sliceWithBreaks);
            view.dispatch(tr);

            return true;
          },

          transformPastedHTML(html) {
            const options = this.options || {
              sanitizeOptions: {
                allowedTags: [
                  'ul',
                  'ol',
                  'li',
                  'br',
                  'p',
                  'div',
                  'span',
                  'b',
                  'i',
                  'strong',
                  'em',
                ],
                allowedAttributes: {
                  span: ['style'],
                  div: ['style'],
                },
              },
            };

            // Fix Slack line breaks (e.g., <span class="c-mrkdwn__br"> → <br>)
            const slackBrFixed = html.replace(
              /<span[^>]*class="[^"]*c-mrkdwn__br[^"]*"[^>]*><\/span>/gi,
              '<br>'
            );

            const cleaned = sanitizeHtml(slackBrFixed, options.sanitizeOptions);
            const dom = new DOMParser().parseFromString(
              cleaned,
              'text/html'
            ).body;

            function processNode(node) {
              if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent;
              }

              if (node.nodeType === Node.ELEMENT_NODE) {
                const tag = node.tagName.toLowerCase();

                if (tag === 'li') {
                  const content = Array.from(node.childNodes)
                    .map(processNode)
                    .join('');
                  return `• ${content}<br>`; // Use bullet or number prefix here
                }

                if (tag === 'ul' || tag === 'ol') {
                  return Array.from(node.childNodes).map(processNode).join('');
                }

                const children = Array.from(node.childNodes)
                  .map(processNode)
                  .join('');

                if (
                  [
                    'p',
                    'div',
                    'section',
                    'article',
                    'br',
                    'hr',
                    'h1',
                    'h2',
                    'h3',
                  ].includes(tag)
                ) {
                  return children ? `${children}<br>` : '<br>';
                }

                return children;
              }

              return '';
            }

            let processed = processNode(dom)
              // .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>') // Remove extra consecutive <br>
              .replace(/(<br\s*\/?>\s*)+$/gi, ''); // Remove trailing <br>

            if (!processed.trim()) processed = '<br>';

            return `<p>${processed}</p>`;
          },
        },
      }),
    ];
  },
});
